import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'lodash';
import {
  AtomicBlockUtils,
  Editor,
  EditorState,
  convertToRaw,
  DefaultDraftBlockRenderMap,
  RichUtils,
  handleKeyCommand
} from "draft-js";

import 'draft-js/dist/Draft.css';
import Immutable from "immutable";

import {
  Box,
  Typography,
  Select,
  FormHelperText,
  Button,
  TextField,
  Grid,
  Modal,
  MenuItem
} from '@mui/material';

import CreateFieldsOptional from './CreateFieldsOptional';

import ModalHeader from '../pop/ModalHeader';
import ModalFooter from '../pop/ModalFooter';
import * as basicConstants from '../../constants/basic';
import * as urlConstants from '../../constants/url';

import useGlobalStatus from '../../hook/useGlobalStatus';

import { CustomFormLabel, BoxBorderGrey, BoxModalWrapper } from '../customComponent';
import { handleColorSystem } from '../customComponent/colorSystem';

import { CREATE_FIELDS_FORMAT } from '../formData/formData';

import { KeyboardArrowDownOutlinedIcon, AddOutlinedIcon, SearchIcon } from '../../constants/images';

import theme from '../../theme';

const initIndexNumber = 200;

const blockRenderMap = Immutable.Map({
  atomic: {
    element: "div"
  }
});

const extendBLockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);

const operatorReg = /([+\-*/()])/g;

const renderExpression = (block) => {

  if (block.getType() === 'atomic') {
    return {
      component: ExpressionChip,
      editable: false
    }
  }

  return null;
}

const ExpressionChip = (props) => {
  const getEntityAt = props.block.getEntityAt(0);
  if (!getEntityAt) return null;

  const entity = props.contentState.getEntity(getEntityAt);

  const { display } = entity.getData();
  const type = entity.getType();

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: type === 'operator' ? '24px' : 'auto',
        margin: '4px',
        padding: '2px 4px',
        backgroundColor: theme.palette.grey.w100,
        borderRadius: '4px',
        fontSize: '0.875rem',
        textAlign: 'center',
        lineHeight: '24px'
      }}
    >
      {display}
    </Box>
  )
}

const checkInput = (el) => {
  const { name, expression, matchBlocks, flatFields, media } = el;

  const errorMsg = {
    fieldName: '',
    expression: ''
  }

  let isError = false;

  if (name === '') {
    errorMsg.fieldName = <FormattedMessage id="createFields.errorMsg.customName" />;
    isError = true;
  }

  if (name !== '' && _.findIndex(flatFields, { name: name.trim() }) > -1) {
    errorMsg.fieldName = <FormattedMessage id="createFields.errorMsg.customNameUnique" />;
    isError = true;
  }

  if (expression.split(' ').join('') === '') {
    errorMsg.expression = <FormattedMessage id="createFields.errorMsg.customFormula" />;
    isError = true;
  }

  if (_.findIndex(flatFields, { expression: expression.trim() }) > -1) {
    errorMsg.expression = <FormattedMessage id="createFields.errorMsg.existedExpression" />;
    isError = true;
  }

  matchBlocks.forEach(el => {
    const matchFields = _.filter(flatFields, f => f.expression === el);
    const isOperator = el.match(operatorReg);

    if (!isOperator && isNaN(Number(el)) && matchFields.length === 0) {
      isError = true;
      errorMsg.expression = <FormattedMessage id="createFields.errorMsg.notUsable" />;
    }
  })

  return {
    isError,
    errorMsg
  }
}

const CreateFields = (props) => {
  const {
    media,
    open,
    optionalFields,
    handleClose,
    handleSave,
    reportLanguage,
    excelSheetSelected
  } = props;

  const location = useLocation();
  const editorRef = useRef(null);
  const intl = useIntl();
  const { locale, setLocale } = useGlobalStatus();

  const flatFields = _.flatMap(optionalFields, f => f.child);
  const [filterOptionFields, setFilterOptionFields] = useState([]);

  const [customFieldName, setCustomFieldName] = useState('');
  const [formatType, setFormatType] = useState('DEFAULT');
  const [editorState, setEditorState] = React.useState(EditorState.createEmpty());

  const [inputErrorMsg, setInputErrorMsg] = useState({ fieldName: '', expression: '' })
  const { colorSystem, mainColor, lightColor, darkColor, w01Color } = handleColorSystem(media);


  const handleKeyCommand = (command) => {
    const newContent = RichUtils.handleKeyCommand(editorState, command);
    if (newContent) {
      setEditorState(newContent);
      return true;
    }

    return false;
  }

  const handleExpression = (editorState) => {
    setEditorState(editorState);
  }

  const handleExpressionInput = (element) => {
    const { inputType, operator, display } = element;

    console.log('display', display);
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      inputType,
      'IMMUTABLE',
      { operator, display }
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity
    });

    setEditorState(AtomicBlockUtils.insertAtomicBlock(
      newEditorState,
      entityKey,
      operator
    ));
  }

  const handleModalSave = () => {
    const blocks = convertToRaw(editorState.getCurrentContent()).blocks;

    let customField;
    if (location.pathname === urlConstants.PAGE_FB_CREATE_DATA) {
      customField = {
        name: customFieldName,
        type: 'CUSTOM',
        format: formatType,
        expression: '',
        indexNumber: initIndexNumber,
      }
    } else if (location.pathname === urlConstants.PAGE_GOOGLE_CREATE_DATA) {
      customField = {
        cid: null,
        cidname: null,
        name: customFieldName,
        type: 'CUSTOM',
        format: formatType,
        expression: '',
        indexNumber: initIndexNumber,
        sheet: ["menu"]
      }
    } else if (location.pathname === urlConstants.PAGE_LINE_CREATE_DATA) {
      customField = {
        name: customFieldName,
        type: 'CUSTOM',
        format: formatType,
        expression: '',
        indexNumber: initIndexNumber,
        sheet: []
      }
    } else if (location.pathname === urlConstants.PAGE_TABOOLA_CREATE_DATA) {
      customField = {
        name: customFieldName,
        type: 'CUSTOM',
        format: formatType,
        expression: '',
        indexNumber: initIndexNumber,
        sheet: excelSheetSelected
      }
    }

    const blocksToArray = blocks.filter(el => el.text !== "").reduce((prev, next) => {
      prev.push(next.text);
      return prev;
    }, []);

    const formatBlocks = blocksToArray.map(el => el.trim());

    const matchBlocks = formatBlocks.map(el => {
      const matchFields = _.filter(flatFields, f => f.name === el || f.expression === el);

      if (Number.isNaN(el)) {
        return Number(el);
      } else if (matchFields.length > 0) {
        return matchFields[0].expression;
      } else {
        return el;
      }
    })

    customField.expression = matchBlocks.join(' ');

    // console.log('customField', customField);
    // console.log('matchBlocks', matchBlocks);

    const checkResult = checkInput({
      name: customFieldName,
      expression: customField.expression,
      matchBlocks: matchBlocks,
      flatFields,
      media: media
    })

    if (checkResult.isError) {
      setInputErrorMsg(checkResult.errorMsg);
      return;
    } else {
      setInputErrorMsg({
        fieldName: '',
        expression: ''
      });
      handleSave(customField, matchBlocks);
      handleClose();
    }

    // console.log('save-customField', customField)
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <BoxModalWrapper sx={{ width: '60%' }} >
        <ModalHeader
          title={<FormattedMessage id="createFields.title.createFields" />}
          handleClose={handleClose}
        />

        <Grid
          container
          sx={{ padding: '24px 40px 40px' }}
        >
          <Grid item xs={5} pr={2} >
            <CustomFormLabel><FormattedMessage id="createFields.label.fields" /></CustomFormLabel>

            <CreateFieldsOptional
              media={media}
              optionalFields={optionalFields}
              reportLanguage={reportLanguage}
              handleExpressionInput={handleExpressionInput}
            />

          </Grid>

          <Grid item xs={7}>
            <Grid container spacing={1}>
              <Grid item xs={8}>
                <CustomFormLabel>
                  <FormattedMessage id="createFields.label.customName" /><span>*</span>
                </CustomFormLabel>

                <TextField
                  placeholder={intl.formatMessage({ id: "createFields.placeholder.enterCustomName" })}
                  value={customFieldName}
                  onChange={(e) => setCustomFieldName(e.target.value)}
                  color={colorSystem}
                  sx={{ width: '100%' }}
                />

                <FormHelperText error sx={{ fontSize: 14 }}>{inputErrorMsg.fieldName}</FormHelperText>

              </Grid>

              <Grid item xs={4}>
                <CustomFormLabel>
                  <FormattedMessage id="createFields.label.format.format" />
                </CustomFormLabel>

                <Select
                  value={formatType}
                  IconComponent={KeyboardArrowDownOutlinedIcon}
                  onChange={(e) => {
                    setFormatType(e.target.value)
                  }}
                  color={colorSystem}
                  sx={{ width: '150px' }}
                >
                  {
                    CREATE_FIELDS_FORMAT.map((el, index) => {
                      return (
                        <MenuItem
                          key={`${el.id}_${index}`}
                          value={el.value}
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: w01Color
                            }
                          }}
                        >
                          <FormattedMessage id={el.id} />
                        </MenuItem>
                      )
                    })
                  }
                </Select>
              </Grid>

            </Grid>

            <Box mt={6}>
              <CustomFormLabel><FormattedMessage id="createFields.label.formula" /><span>*</span></CustomFormLabel>
              <Typography variant="body_sm"><FormattedMessage id="createFields.label.clickMetrics" /></Typography>

              <Box my={1}>
                {
                  basicConstants.operators.map((el, index) => {
                    return (
                      <Button
                        key={`${el.operators}_${index}`}
                        variant="outlined-square"
                        sx={{ marginRight: '8px' }}
                        onClick={() => {
                          handleExpressionInput({ inputType: 'operator', operator: el.operator, display: el.display });
                        }}
                      >
                        {el.operator}
                      </Button>
                    )
                  })
                }
              </Box>

              <BoxBorderGrey
                sx={{
                  overflow: 'auto',
                  width: '100%',
                  height: '130px',
                  padding: '8px'
                }}
              >
                <Editor
                  ref={editorRef}
                  blockRendererFn={renderExpression}
                  blockRenderMap={extendBLockRenderMap}
                  editorState={editorState}
                  onChange={handleExpression}
                  handleKeyCommand={handleKeyCommand}
                />
              </BoxBorderGrey>

              <FormHelperText error sx={{ fontSize: 14 }}>{inputErrorMsg.expression}</FormHelperText>
            </Box>

          </Grid>

        </Grid>

        <ModalFooter
          media={media}
          checkBottonContent={<FormattedMessage id="createFields.button.saveMetric" />}
          handelCancel={handleClose}
          handleSubmit={handleModalSave}
        />

      </BoxModalWrapper>
    </Modal>
  )
}

export default CreateFields;